import { mergeAdditionalRolodexFilters } from 'features/drilldown/components/table/mergeAdditionalRolodexFilters'
import { useFocusItems } from 'features/drilldown/data/DrilldownFilterContext'
import {
  NII_CAMPAIGN_TITLE,
  NII_RESULTS_FILTERS,
} from 'features/drilldown/panels/impl/niiLibraResultsTable/NiiLibraResultsPanel'
import {
  aggregateNiiCalculatorSavings,
  getDistinctSiteIdsFromTickets,
  getNiiCalculatorTarRoiBySiteFromGroup,
  NiiCalculatorAggregate,
} from 'features/drilldown/panels/impl/niiRoi/niiRoiCalculatorModel'
import { useFilteredEntryQuery } from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import { usePaginatedQuery } from 'features/fde/helpers/usePaginatedQuery'
import {
  LibraTicket,
  LibraTicketGroup,
  parseLibraTicketEntry,
  parseLibraTicketGroupEntry,
} from 'features/libra/ticket/libraTicketsApi'
import { getLatestVersions } from 'features/libra/ticket/ticketVersioning'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import { GeckoEntry, RolodexEntryType, RolodexHistogramFilter } from 'types'

function parseTickets(entries: ReadonlyArray<GeckoEntry>): LibraTicket[] {
  return entries
    .filter((entry) => entry.type === RolodexEntryType.LIBRA_TICKET)
    .map(parseLibraTicketEntry)
    .filter((ticket): ticket is LibraTicket => ticket !== undefined)
}

function parseGroups(entries: ReadonlyArray<GeckoEntry>): LibraTicketGroup[] {
  return entries
    .filter((entry) => entry.type === RolodexEntryType.LIBRA_TICKET_GROUP)
    .flatMap((entry) => {
      try {
        return [parseLibraTicketGroupEntry(entry)]
      } catch (error) {
        console.error(`Failed to parse libra ticket group ${entry.id}:`, error)
        return []
      }
    })
}

const NII_GROUP_FILTERS: ReadonlyArray<RolodexHistogramFilter> = [
  {
    type: RolodexEntryType.LIBRA_TICKET_GROUP,
    field: 'data',
    key: 'title',
    op: '=',
    value: NII_CAMPAIGN_TITLE,
  },
]

const EMPTY_ROI = aggregateNiiCalculatorSavings([], undefined)
const LOAD_ALL_PAGINATION = { pageSize: 10_000, currentPage: 1 }

function focusItemsToFilters(
  focusItems: ReturnType<typeof useFocusItems>[0]
): ReadonlyArray<RolodexHistogramFilter> {
  if (!focusItems) return []
  return [...focusItems.entries()].flatMap(([type, ids]) =>
    ids.length
      ? [
          {
            type,
            field: 'id',
            op: 'in',
            value: [...ids],
          } satisfies RolodexHistogramFilter,
        ]
      : []
  )
}

export function useNiiCampaignRoi(): {
  tickets: ReadonlyArray<LibraTicket>
  group: LibraTicketGroup | undefined
  roi: NiiCalculatorAggregate
  siteOptions: ReadonlyArray<{ value: string; label: string }>
  siteIds: ReadonlyArray<string>
  isLoading: boolean
  refreshTickets: () => void
  refreshGroup: () => void
} {
  const { query, isLoading: queryLoading } = useFilteredEntryQuery(
    RolodexEntryType.LIBRA_TICKET
  )
  const [focusItems] = useFocusItems()

  const focusFilters = useMemo(
    () => focusItemsToFilters(focusItems),
    [focusItems]
  )

  const ticketQuery = useMemo(
    () =>
      mergeAdditionalRolodexFilters(
        mergeAdditionalRolodexFilters(query, NII_RESULTS_FILTERS),
        focusFilters
      ),
    [focusFilters, query]
  )

  const { query: ticketEntryQuery, isLoading: ticketPlanLoading } =
    usePaginatedQuery(
      ticketQuery,
      LOAD_ALL_PAGINATION,
      RolodexEntryType.LIBRA_TICKET
    )

  const {
    entries: ticketEntries,
    isLoading: ticketsLoading,
    forceRefresh: refreshTickets,
  } = useRolodexEntries(ticketEntryQuery)

  const groupQuery = useMemo(
    () => ({
      type: RolodexEntryType.LIBRA_TICKET_GROUP,
      filters: [...NII_GROUP_FILTERS],
    }),
    []
  )

  const { query: groupEntryQuery, isLoading: groupPlanLoading } =
    usePaginatedQuery(
      groupQuery,
      LOAD_ALL_PAGINATION,
      RolodexEntryType.LIBRA_TICKET_GROUP
    )

  const {
    entries: groupEntries,
    isLoading: groupLoading,
    forceRefresh: refreshGroup,
  } = useRolodexEntries(groupEntryQuery)

  const tickets = useMemo(
    () =>
      getLatestVersions(
        parseTickets(ticketEntries).filter((t) => t.workflowComplete)
      ),
    [ticketEntries]
  )

  const group = useMemo(() => parseGroups(groupEntries)[0], [groupEntries])

  const siteIds = useMemo(
    () => getDistinctSiteIdsFromTickets(tickets),
    [tickets]
  )

  const { entries: siteEntries, isLoading: sitesLoading } = useRolodexEntries(
    useMemo(
      () =>
        siteIds.length
          ? {
              type: RolodexEntryType.SITE,
              ids: [...siteIds],
            }
          : [],
      [siteIds]
    )
  )

  const siteOptions = useMemo(
    () =>
      siteEntries
        .map((entry) => ({
          value: entry.id,
          label:
            typeof entry.data.name === 'string' && entry.data.name
              ? entry.data.name
              : entry.id,
        }))
        .toSorted((a, b) => a.label.localeCompare(b.label)),
    [siteEntries]
  )

  const roi = useMemo(
    () =>
      group
        ? aggregateNiiCalculatorSavings(
            tickets,
            getNiiCalculatorTarRoiBySiteFromGroup(group)
          )
        : EMPTY_ROI,
    [group, tickets]
  )

  return {
    tickets,
    group,
    roi,
    siteOptions,
    siteIds,
    isLoading:
      queryLoading ||
      ticketPlanLoading ||
      ticketsLoading ||
      groupPlanLoading ||
      groupLoading ||
      sitesLoading,
    refreshTickets,
    refreshGroup,
  }
}
